<template>
  <v-container fluid class="mt-16 px-6">
    <v-card flat>
      <v-card-title class="d-flex align-center flex-wrap">
        <h2>Sales Target Dashboard</h2>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="month"
          type="month"
          label="Month"
          dense
          hide-details
          style="max-width: 180px"
          class="mr-3"
          @change="load"
        ></v-text-field>
        <v-btn v-if="isAdmin" color="primary" class="mr-2" @click="openTargets">
          <v-icon left>mdi-target</v-icon>Manage Targets
        </v-btn>
        <v-btn icon x-large @click="load" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div
          v-if="!loading && dashboard.length === 0"
          class="text-center py-10 grey--text"
        >
          <v-icon size="48" color="grey lighten-1"
            >mdi-account-tie-outline</v-icon
          >
          <p class="mt-2">No sales agents in this branch yet.</p>
        </div>

        <v-row>
          <v-col
            v-for="card in dashboard"
            :key="card.agent_id"
            cols="12"
            :md="dashboard.length > 1 ? 6 : 12"
          >
            <v-card outlined class="pa-4">
              <div class="d-flex align-center mb-2">
                <v-icon left color="primary">mdi-account-tie</v-icon>
                <span class="text-h6">{{ card.agent_name }}</span>
                <v-spacer></v-spacer>
                <v-chip
                  small
                  :color="
                    card.minimum_amount !== null &&
                    card.closed_amount >= card.minimum_amount
                      ? 'green'
                      : 'grey'
                  "
                  dark
                  v-if="card.minimum_amount !== null"
                >
                  {{
                    card.closed_amount >= card.minimum_amount
                      ? "Minimum met"
                      : "Below minimum"
                  }}
                </v-chip>
              </div>

              <!-- Headline: % remaining -->
              <v-row align="center" class="mb-1">
                <v-col cols="12" sm="5" class="text-center">
                  <div class="text-caption grey--text">
                    % REMAINING TO TARGET
                  </div>
                  <div
                    class="font-weight-bold"
                    :class="headlineClass(card)"
                    style="font-size: 3.2rem; line-height: 1.1"
                  >
                    {{ card.pct_remaining }}%
                  </div>
                  <div class="text-caption">
                    ₱{{ money(card.amount_remaining) }} to go
                  </div>
                </v-col>
                <v-col cols="12" sm="7">
                  <div class="d-flex justify-space-between text-caption">
                    <span>Closed ₱{{ money(card.closed_amount) }}</span>
                    <span>{{ card.pct_achieved }}% of target</span>
                  </div>
                  <v-progress-linear
                    :value="Math.min(card.pct_achieved, 100)"
                    height="18"
                    rounded
                    :color="barColor(card)"
                    class="my-1"
                  ></v-progress-linear>
                  <div
                    class="d-flex justify-space-between text-caption grey--text"
                  >
                    <span>Target ₱{{ money(card.target_amount) }}</span>
                    <span v-if="card.minimum_amount !== null"
                      >Min ₱{{ money(card.minimum_amount) }}</span
                    >
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-3"></v-divider>

              <!-- Stats -->
              <v-row dense class="text-center">
                <v-col cols="6" sm="3">
                  <div class="text-caption grey--text">Deals Closed</div>
                  <div class="text-h6">{{ card.deals_count }}</div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="text-caption grey--text">Avg Deal</div>
                  <div class="text-h6">₱{{ money(card.avg_deal) }}</div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="text-caption grey--text">
                    Pace / day
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon x-small v-on="on"
                          >mdi-information-outline</v-icon
                        >
                      </template>
                      <span
                        >Needed per remaining working day ({{
                          card.remaining_working_days
                        }}
                        left) to hit target</span
                      >
                    </v-tooltip>
                  </div>
                  <div class="text-h6">₱{{ money(card.pace_per_day) }}</div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="text-caption grey--text">
                    Pipeline ({{ card.open_leads_count }} open)
                  </div>
                  <div class="text-h6">₱{{ money(card.pipeline_value) }}</div>
                </v-col>
              </v-row>

              <v-divider class="my-3"></v-divider>

              <!-- 6-month history -->
              <div class="text-caption grey--text mb-2">
                Last 6 months — closed vs target
              </div>
              <div
                v-for="h in card.history"
                :key="h.month"
                class="d-flex align-center mb-1"
              >
                <span class="text-caption" style="width: 64px">{{
                  h.label
                }}</span>
                <v-progress-linear
                  :value="
                    h.target > 0
                      ? Math.min((h.closed / h.target) * 100, 100)
                      : h.closed > 0
                      ? 100
                      : 0
                  "
                  height="14"
                  rounded
                  :color="
                    h.target > 0 && h.closed >= h.target ? 'green' : 'blue'
                  "
                  class="mx-2"
                  style="flex: 1"
                ></v-progress-linear>
                <span class="text-caption text-right" style="width: 150px">
                  ₱{{ money(h.closed) }}
                  <span class="grey--text">/ ₱{{ money(h.target) }}</span>
                </span>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <p class="caption grey--text text-right pr-4 pb-2 mb-0">
        SalesDashboard.vue
      </p>
    </v-card>

    <!-- Manage Targets (admin) -->
    <v-dialog v-model="targetsDialog" width="620" scrollable>
      <v-card>
        <v-card-title>
          <h3>Manage Targets — {{ month }}</h3>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height: 60vh">
          <p v-if="targets.length === 0" class="grey--text mt-3">
            No sales agents in this branch.
          </p>
          <v-row
            v-for="t in targets"
            :key="t.agent_id"
            dense
            align="center"
            class="mt-1"
          >
            <v-col cols="12" sm="4">{{ t.agent_name }}</v-col>
            <v-col cols="6" sm="4">
              <v-text-field
                v-model.number="t.target_amount"
                type="number"
                label="Target ₱"
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="6" sm="4">
              <v-text-field
                v-model.number="t.minimum_amount"
                type="number"
                label="Minimum ₱"
                dense
                hide-details
                clearable
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="primary" @click="saveTargets" :loading="savingTargets"
            >Save All</v-btn
          >
          <v-btn text @click="targetsDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <span class="caption grey--text"
            >min 250,000 / target 300,000 planned</span
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  data() {
    return {
      month: "",
      loading: false,
      targetsDialog: false,
      savingTargets: false,
    };
  },
  computed: {
    ...mapGetters({
      dashboard: "sales/dashboard",
      targets: "sales/targets",
      user: "auth/user",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
    }),
    isAdmin() {
      // "View all agents' leads" is a pure hat-permission gate
      // (leads.view_all, managed on Role Restrictions).
      return this.hasPermission("leads.view_all");
    },
  },
  methods: {
    ...mapActions({
      get_dashboard: "sales/get_dashboard",
      get_targets: "sales/get_targets",
      save_target: "sales/save_target",
    }),
    money(v) {
      return Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    headlineClass(card) {
      if (card.pct_achieved >= 100) return "green--text";
      if (card.pct_achieved >= 50) return "orange--text";
      return "red--text";
    },
    barColor(card) {
      if (card.pct_achieved >= 100) return "green";
      if (card.pct_achieved >= 50) return "amber darken-1";
      return "red";
    },
    async load() {
      this.loading = true;
      try {
        await this.get_dashboard(this.month);
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e?.response?.data?.message || "Failed to load dashboard.",
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.loading = false;
      }
    },
    async openTargets() {
      try {
        await this.get_targets(this.month);
        this.targetsDialog = true;
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e?.response?.data?.message || "Failed to load targets.",
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    async saveTargets() {
      this.savingTargets = true;
      try {
        for (const t of this.targets) {
          await this.save_target({
            agent_id: t.agent_id,
            month: this.month,
            target_amount: t.target_amount || 0,
            minimum_amount:
              t.minimum_amount === "" || t.minimum_amount === null
                ? null
                : t.minimum_amount,
          });
        }
        this.targetsDialog = false;
        await this.load();
        this.$alert({
          title: "Saved",
          message: "Targets updated.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e?.response?.data?.message || "Failed to save targets.",
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.savingTargets = false;
      }
    },
  },
  watch: {
    // Hat-based users only resolve leads.view_all once permissions finish
    // loading, so re-load the dashboard (admin sees all agents) when it
    // flips true after that.
    isAdmin(v) {
      if (v) this.load();
    },
  },
  created() {
    const now = new Date();
    this.month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
    this.load();
  },
};
</script>
